import { DOWNLOAD_TYPE, inlineType } from "../../core/attachment-type";
import type {
  NewTicket,
  Ticket,
  TicketAttachment,
  TicketComment,
  TicketDetail,
} from "../../core/tickets";
import type { SupportProjectsView, SupportStatusView } from "../../node/support/types";

/** How the browser reaches the host: `fetch`-compatible, e.g. a wrapper adding the session. */
export type SupportFetch = (input: string, init?: RequestInit) => Promise<Response>;

export type SupportApiOptions = {
  /** Mount point of the host contract. Default `/api/support`. */
  basePath?: string;
  /** Defaults to `window.fetch` with `credentials: "same-origin"`. */
  fetch?: SupportFetch;
};

export type NewReport = Pick<NewTicket, "title" | "description" | "priority" | "type" | "area">;

const seg = encodeURIComponent;

/**
 * Typed client for the host routes `/api/support/*` (STANDARD.md §4). The browser only
 * ever talks to its own backend: the dashboard token never reaches the page.
 */
export const createSupportApi = (options: SupportApiOptions = {}) => {
  const base = (options.basePath ?? "/api/support").replace(/\/+$/, "");
  const doFetch: SupportFetch =
    options.fetch ?? ((input, init) => fetch(input, { credentials: "same-origin", ...init }));

  const call = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await doFetch(`${base}${path}`, init);
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = (await response.json()) as { message?: unknown };
        if (body.message) message = String(body.message);
      } catch {
        // No JSON body: keep the status.
      }
      throw new Error(message);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };
  const post = <T>(path: string, body?: unknown) =>
    call<T>(path, {
      method: "POST",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

  return {
    status: () => call<SupportStatusView>("/status"),
    connect: (draft: { dashboardUrl?: string; project?: string }) =>
      post<SupportStatusView>("/connect", draft),
    poll: () => post<SupportStatusView>("/poll"),
    disconnect: () => post<SupportStatusView>("/disconnect"),
    /** Projects offered by a dashboard, discovered through the host. */
    projects: (dashboardUrl?: string) =>
      call<SupportProjectsView>(
        `/projects${dashboardUrl ? `?dashboardUrl=${seg(dashboardUrl)}` : ""}`,
      ),
    tickets: () => call<Ticket[]>("/tickets"),
    ticket: (id: string) => call<TicketDetail>(`/tickets/${seg(id)}`),
    report: (input: NewReport) => post<Ticket>("/report", input),
    comment: (id: string, text: string) =>
      post<TicketComment>(`/tickets/${seg(id)}/comments`, { text }),
    upload: (id: string, file: Blob, commentId?: string) => {
      const form = new FormData();
      form.append("image", file, (file as File).name || "allegato");
      if (commentId) form.append("commentId", commentId);
      return call<TicketAttachment>(`/tickets/${seg(id)}/attachments`, {
        method: "POST",
        body: form,
      });
    },
    /** The route serving an attachment's bytes (needs the host session, so not an <img src> for cross-auth hosts). */
    attachmentPath: (id: string, attachmentId: string) =>
      `${base}/tickets/${seg(id)}/attachments/${seg(attachmentId)}`,
    /** Downloads an attachment through the host and returns an object URL (revoke it). */
    attachmentUrl: async (id: string, attachmentId: string) => {
      const response = await doFetch(`${base}/tickets/${seg(id)}/attachments/${seg(attachmentId)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // The blob keeps the type the server sent: a host on an older package may still
      // answer `text/html`, which `window.open` would render as a page of this origin.
      const blob = await response.blob();
      const type = inlineType(blob.type) ?? DOWNLOAD_TYPE;
      return URL.createObjectURL(new Blob([blob], { type }));
    },
    error: (body: { message: string; stack?: string; context?: Record<string, unknown> }) =>
      post<void>("/error", body),
  };
};

export type SupportApi = ReturnType<typeof createSupportApi>;
